<template>
  <div class="quotes-shell" :class="{ 'quotes-shell--hidden': hideWhenEmpty && !loading && !hasAnyQuotes }">
    <!-- Filters & Search Toolbar -->
    <div v-if="showFilters && !loading && hasAnyQuotes" class="quotes-toolbar">
      <div class="quotes-toolbar__row">
        <!-- Search -->
        <div class="quotes-toolbar__search">
          <v-icon size="18" color="#94A3B8" class="tw-mr-2">mdi-magnify</v-icon>
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search by client, vehicle, quote ref..."
            class="quotes-toolbar__search-input"
          />
          <button
            v-if="searchQuery"
            type="button"
            class="quotes-toolbar__clear-btn"
            @click="searchQuery = ''"
          >
            <v-icon size="16" color="#94A3B8">mdi-close</v-icon>
          </button>
        </div>

        <!-- Product filter -->
        <div class="quotes-toolbar__field">
          <label class="quotes-toolbar__label">Product</label>
          <select v-model="selectedProduct" class="quotes-toolbar__select">
            <option value="all">All Products</option>
            <option value="motor">Motor Insurance</option>
            <option value="pa">Students PA</option>
            <option value="travel">Travel Insurance</option>
            <option v-if="healthQuotes.length" value="health">Medical Insurance</option>
            <option v-if="dpQuotes.length" value="dp">Domestic Package</option>
          </select>
        </div>

        <!-- Date From -->
        <div class="quotes-toolbar__field">
          <label class="quotes-toolbar__label">From</label>
          <div class="quotes-toolbar__date-wrap">
            <v-icon size="16" color="#94A3B8" class="quotes-toolbar__date-icon">mdi-calendar</v-icon>
            <input
              v-model="dateFrom"
              type="date"
              class="quotes-toolbar__date-input"
            />
          </div>
        </div>

        <!-- Date To -->
        <div class="quotes-toolbar__field">
          <label class="quotes-toolbar__label">To</label>
          <div class="quotes-toolbar__date-wrap">
            <v-icon size="16" color="#94A3B8" class="quotes-toolbar__date-icon">mdi-calendar</v-icon>
            <input
              v-model="dateTo"
              type="date"
              class="quotes-toolbar__date-input"
            />
          </div>
        </div>

        <!-- Reset Button -->
        <button
          v-if="hasActiveFilters"
          type="button"
          class="quotes-toolbar__reset-btn"
          @click="resetFilters"
        >
          <v-icon size="14" color="#E53E3E" class="tw-mr-1">mdi-refresh</v-icon>
          Reset
        </button>
      </div>

      <!-- Filter status bar -->
      <div class="quotes-toolbar__meta">
        <span class="quotes-toolbar__count">
          Showing <strong>{{ filteredItems.length }}</strong> of <strong>{{ allDisplayItems.length }}</strong> quotes
        </span>
        <span v-if="hasActiveFilters" class="quotes-toolbar__filtered-tag">
          Filtered
        </span>
      </div>
    </div>

    <!-- Loading skeleton -->
    <div v-if="loading" class="quotes-list quotes-list--loading" aria-label="Loading your quotes">
      <article
        v-for="n in 2"
        :key="n"
        class="quote-card quote-card--skeleton"
      >
        <!-- Quote header -->
        <div class="quote-card__hero">
          <div class="quotes-skeleton quotes-skeleton__icon"></div>

          <div class="quote-card__intro">
            <div class="quotes-skeleton quotes-skeleton__title"></div>
            <div class="quotes-skeleton quotes-skeleton__subtitle"></div>
          </div>
        </div>

        <!-- Quote details -->
        <div class="quote-card__details">
          <div class="quote-detail">
            <div class="quotes-skeleton quotes-skeleton__label"></div>
            <div class="quotes-skeleton quotes-skeleton__value"></div>
          </div>

          <div class="quote-detail">
            <div class="quotes-skeleton quotes-skeleton__label"></div>
            <div class="quotes-skeleton quotes-skeleton__value"></div>
          </div>

          <div class="quote-detail">
            <div class="quotes-skeleton quotes-skeleton__label"></div>
            <div class="quotes-skeleton quotes-skeleton__value"></div>
          </div>
        </div>

        <!-- Action button -->
        <div class="quote-card__actions">
          <div class="quotes-skeleton quotes-skeleton__button"></div>
        </div>
      </article>
    </div>

    <!-- Filtered Empty State -->
    <div v-else-if="hasAnyQuotes && filteredItems.length === 0" class="quotes-empty quotes-empty--filtered">
      <div class="quotes-empty__icon">
        <v-icon size="48" color="#94A3B8">mdi-filter-remove-outline</v-icon>
      </div>
      <h3 class="quotes-empty__title">No matching quotes found</h3>
      <p class="quotes-empty__text">
        No active quotes match your filter or date range. Try clearing your filters or choosing different dates.
      </p>
      <button type="button" class="quotes-empty__cta-btn" @click="resetFilters">
        Reset filters
      </button>
    </div>

    <!-- Quote Cards List -->
    <div v-else-if="hasAnyQuotes" class="quotes-list">
      <article
        v-for="item in displayItems"
        :id="item.domId"
        :key="item.key"
        class="quote-card"
      >
        <div class="quote-card__hero">
          <div class="quote-card__icon" :style="{ background: item.iconBg }">
            <v-icon :color="item.accent" size="28">{{ item.icon }}</v-icon>
          </div>
          <div class="quote-card__intro">
            <div class="quote-card__title-row">
              <h3 class="quote-card__title">{{ item.productLabel }}</h3>
              <span class="quote-card__badge">{{ item.statusLabel }}</span>
              <span v-if="item.dateFormatted" class="quote-card__date">
                <v-icon x-small color="#64748B" class="tw-mr-1">mdi-calendar-blank-outline</v-icon>
                {{ item.dateFormatted }}
              </span>
            </div>
            <p class="quote-card__subtitle">{{ item.subtitle }}</p>
          </div>
        </div>

        <div class="quote-card__details">
          <div v-for="(detail, di) in item.details" :key="di" class="quote-detail">
            <span class="quote-detail__label">{{ detail.label }}</span>
            <span class="quote-detail__value">{{ detail.value }}</span>
          </div>
        </div>

        <div v-if="item.showStepper" class="quote-card__stepper">
          <v-stepper alt-labels>
            <v-stepper-header>
              <v-stepper-step step="1" :complete="item.progress > 0">Personal</v-stepper-step>
              <v-divider :color="item.progress > 0 ? 'secondary' : ''" />
              <v-stepper-step step="2" :complete="item.progress > 35">Vehicle</v-stepper-step>
              <v-divider :color="item.progress > 35 ? 'secondary' : ''" />
              <v-stepper-step step="3" :complete="item.progress > 67">Summary</v-stepper-step>
              <v-divider :color="item.progress > 67 ? 'secondary' : ''" />
              <v-stepper-step step="4" :complete="item.progress === 100">Payment</v-stepper-step>
            </v-stepper-header>
          </v-stepper>
        </div>

        <div class="quote-card__actions">
          <v-btn
            color="primary"
            depressed
            :id="item.domId + '-resume'"
            :loading="resumingKey === item.key"
            :disabled="!!resumingKey && resumingKey !== item.key"
            @click="resumeItem(item)"
          >
            {{ item.ctaLabel }}
            <v-icon right small color="#fff">mdi-chevron-right</v-icon>
          </v-btn>
        </div>
      </article>

      <!-- Pagination Controls -->
      <div v-if="paginated && totalPages > 1" class="quotes-pagination">
        <span class="quotes-pagination__info">
          Showing {{ paginationStart }}-{{ paginationEnd }} of {{ filteredItems.length }}
        </span>
        <div class="quotes-pagination__controls">
          <button
            type="button"
            class="quotes-pagination__btn"
            :disabled="currentPage <= 1"
            @click="goToPage(currentPage - 1)"
          >
            Previous
          </button>
          <div class="quotes-pagination__pages">
            <button
              v-for="p in visiblePageNumbers"
              :key="p"
              type="button"
              class="quotes-pagination__page-btn"
              :class="{ 'quotes-pagination__page-btn--active': p === currentPage }"
              @click="goToPage(p)"
            >
              {{ p }}
            </button>
          </div>
          <button
            type="button"
            class="quotes-pagination__btn"
            :disabled="currentPage >= totalPages"
            @click="goToPage(currentPage + 1)"
          >
            Next
          </button>
        </div>
      </div>
    </div>

    <div v-else class="quotes-empty">
      <div class="quotes-empty__icon">
        <v-icon size="48" color="#94A3B8">mdi-file-document-outline</v-icon>
      </div>
      <h3 class="quotes-empty__title">{{ isAgent ? 'No active quotes' : 'No saved quotes' }}</h3>
      <p class="quotes-empty__text">
        {{ isAgent ? 'Start a quote from Purchase Cover and pick up where you left off here.' : 'Start a quote from Products and pick up where you left off here.' }}
      </p>
      <router-link :to="{ name: isAgent ? 'AgentProductSelect' : 'CustomerProductSelect' }" class="quotes-empty__cta">
        {{ isAgent ? 'Purchase cover' : 'Browse products' }}
      </router-link>
    </div>
  </div>
</template>

<script>
import cookie from 'vue-cookies';
import { mapGetters } from 'vuex';
import { PA_COURSE_DURATIONS } from '@/constants/paStudentsRateCard';
import { getCognitoIdentityProfile } from '@/utils/paChannel';
import { USER_TYPE } from '@/utils/const';
import { isPaQuoteIssued, isPaQuoteAwaitingIssuance } from '@/utils/paQuoteResume';
import {
  resumeDpQuote,
  resumeHealthQuote,
  resumeMotorQuote,
  resumePaQuote,
  resumeTravelQuote,
} from '@/utils/resumeQuote';

const LABEL_QUOTE_REF = 'Quote ref.';
const CTA_RESUME = 'Resume quote';
const STATUS_IN_PROGRESS = 'In progress';
const STATUS_READY_TO_PAY = 'Ready to pay';

function getQuoteRawDate(quote) {
  if (!quote) return null;
  return quote.createdAt
    || quote.dateCreated
    || quote.createDate
    || quote.createdDate
    || quote.created_at
    || quote.updatedAt
    || quote.updated_at
    || quote.travelStartDate
    || quote.startDate
    || quote.coverFrom
    || quote.date
    || null;
}

function parseQuoteDate(quote) {
  const raw = getQuoteRawDate(quote);
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatQuoteDate(quote) {
  const d = parseQuoteDate(quote);
  if (!d) return null;
  const day = String(d.getDate()).padStart(2, '0');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = months[d.getMonth()];
  const year = d.getFullYear();
  return `${day} ${month} ${year}`;
}

function buildQuoteItemDate(quote) {
  const parsed = parseQuoteDate(quote);
  return {
    rawDate: parsed ? parsed.toISOString().slice(0, 10) : null,
    timestamp: parsed ? parsed.getTime() : 0,
    dateFormatted: formatQuoteDate(quote),
  };
}

function formatTravelPremium(quote) {
  if (quote.kshAmount != null) {
    return `KES ${Number(quote.kshAmount).toLocaleString('en-KE')}`;
  }
  if (quote.premium != null) {
    return `USD ${quote.premium}`;
  }
  return '—';
}

export default {
  name: 'Quotes',
  props: {
    /** Hide the whole block on dashboard when there are no quotes (still loads data). */
    hideWhenEmpty: {
      type: Boolean,
      default: false,
    },
    /** Max cards to show (0 = no limit). */
    limit: {
      type: Number,
      default: 0,
    },
    /** Target audience ('agent' or 'customer'). Defaults to cookie user type. */
    audience: {
      type: String,
      default: null,
    },
    /** Show filters toolbar (search, product, date range). */
    showFilters: {
      type: Boolean,
      default: false,
    },
    /** Enable pagination. */
    paginated: {
      type: Boolean,
      default: false,
    },
    /** Page size for pagination. */
    pageSize: {
      type: Number,
      default: 10,
    },
  },
  data() {
    return {
      resumingKey: null,
      searchQuery: '',
      selectedProduct: 'all',
      dateFrom: '',
      dateTo: '',
      currentPage: 1,
    };
  },
  computed: {
    ...mapGetters('auth', ['authenticatedUser']),
    ...mapGetters('portalCache', [
      'customerQuotes',
      'customerQuotesLoading',
      'agentQuotes',
      'agentQuotesLoading',
    ]),
    isAgent() {
      if (this.audience === 'agent') return true;
      if (this.audience === 'customer') return false;
      return cookie.get(USER_TYPE) === 'agent';
    },
    activeQuotesSource() {
      return this.isAgent ? this.agentQuotes : this.customerQuotes;
    },
    loading() {
      return this.isAgent ? this.agentQuotesLoading : this.customerQuotesLoading;
    },
    quotes() {
      return this.activeQuotesSource?.motor || [];
    },
    paQuotes() {
      return this.activeQuotesSource?.pa || [];
    },
    healthQuotes() {
      return this.activeQuotesSource?.health || [];
    },
    dpQuotes() {
      return this.activeQuotesSource?.dp || [];
    },
    travelQuotes() {
      return this.activeQuotesSource?.travel || [];
    },
    identity() {
      return getCognitoIdentityProfile(this.authenticatedUser);
    },
    hasAnyQuotes() {
      return this.allDisplayItems.length > 0;
    },
    hasActiveFilters() {
      return Boolean(
        (this.searchQuery && this.searchQuery.trim())
        || (this.selectedProduct && this.selectedProduct !== 'all')
        || this.dateFrom
        || this.dateTo,
      );
    },
    filteredItems() {
      let list = this.allDisplayItems;

      if (this.selectedProduct && this.selectedProduct !== 'all') {
        list = list.filter((item) => item.type === this.selectedProduct);
      }

      if (this.searchQuery && this.searchQuery.trim()) {
        const q = this.searchQuery.trim().toLowerCase();
        list = list.filter((item) => {
          const matchTitle = item.productLabel?.toLowerCase().includes(q);
          const matchSubtitle = item.subtitle?.toLowerCase().includes(q);
          const matchDetails = item.details?.some((d) => d.value?.toString().toLowerCase().includes(q));
          const matchKey = item.key?.toLowerCase().includes(q);
          return matchTitle || matchSubtitle || matchDetails || matchKey;
        });
      }

      if (this.dateFrom) {
        list = list.filter((item) => item.rawDate && item.rawDate >= this.dateFrom);
      }

      if (this.dateTo) {
        list = list.filter((item) => item.rawDate && item.rawDate <= this.dateTo);
      }

      return list;
    },
    totalPages() {
      if (!this.paginated || this.pageSize <= 0) return 1;
      return Math.max(1, Math.ceil(this.filteredItems.length / this.pageSize));
    },
    displayItems() {
      if (this.paginated && this.pageSize > 0) {
        const start = (this.currentPage - 1) * this.pageSize;
        return this.filteredItems.slice(start, start + this.pageSize);
      }
      if (this.limit > 0) {
        return this.filteredItems.slice(0, this.limit);
      }
      return this.filteredItems;
    },
    paginationStart() {
      if (!this.filteredItems.length) return 0;
      return (this.currentPage - 1) * this.pageSize + 1;
    },
    paginationEnd() {
      return Math.min(this.currentPage * this.pageSize, this.filteredItems.length);
    },
    visiblePageNumbers() {
      const pages = [];
      const total = this.totalPages;
      const current = this.currentPage;
      const delta = 2;
      for (let i = Math.max(1, current - delta); i <= Math.min(total, current + delta); i += 1) {
        pages.push(i);
      }
      return pages;
    },
    allDisplayItems() {
      const items = [];

      (this.quotes || []).forEach((quote, i) => {
        const risk = this.filterCar(quote);
        const car = risk?.car || {};
        items.push({
          key: `motor-${quote.quoteId || i}`,
          domId: `quotes-motor-${i}`,
          productLabel: 'Motor Insurance',
          statusLabel: STATUS_IN_PROGRESS,
          subtitle: quote.details?.client?.fullName || this.authenticatedUser?.name || 'Motor quote',
          accent: '#184679',
          iconBg: 'linear-gradient(135deg, #E8EEF5 0%, #F0F4F8 100%)',
          icon: 'mdi-car-outline',
          details: [
            { label: 'Vehicle', value: [car.make, car.model, car.yearOfManufacture].filter(Boolean).join(' ') || '—' },
            { label: 'Phone', value: quote.details?.client?.mobileNumber || this.authenticatedUser?.phone_number || '—' },
            { label: LABEL_QUOTE_REF, value: quote.quoteId ? `#${quote.quoteId}` : '—' },
          ],
          progress: this.progress(quote),
          showStepper: true,
          ctaLabel: CTA_RESUME,
          quote,
          type: 'motor',
          ...buildQuoteItemDate(quote),
        });
      });

      (this.paQuotes || []).forEach((quote, i) => {
        const duration = PA_COURSE_DURATIONS.find((d) => d.code === quote.courseDuration);
        const paStatus = quote.stepCode === 'PA_READY_FOR_PAYMENT' ? STATUS_READY_TO_PAY : STATUS_IN_PROGRESS;
        items.push({
          key: `pa-${quote.quoteId || i}`,
          domId: `quotes-pa-${i}`,
          productLabel: 'Students PA',
          statusLabel: paStatus,
          subtitle: quote.proposerName || quote.optionName || 'Personal Accident',
          accent: '#7c3aed',
          iconBg: 'linear-gradient(135deg, #EDE9FE 0%, #F5F3FF 100%)',
          icon: 'mdi-shield-account',
          details: [
            { label: 'Plan', value: quote.optionName || '—' },
            { label: 'Duration', value: duration?.label || quote.courseDuration || '—' },
            { label: 'Premium', value: quote.premium != null ? `KES ${Number(quote.premium).toLocaleString('en-KE')}` : '—' },
          ],
          progress: 0,
          showStepper: false,
          ctaLabel: CTA_RESUME,
          quote,
          type: 'pa',
          ...buildQuoteItemDate(quote),
        });
      });

      (this.healthQuotes || []).forEach((quote, i) => {
        items.push({
          key: `health-${quote.id || i}`,
          domId: `quotes-health-${i}`,
          productLabel: 'Medical Insurance',
          statusLabel: 'Draft',
          subtitle: 'Pacis Amani quote',
          accent: '#0d9488',
          iconBg: 'linear-gradient(135deg, #CCFBF1 0%, #F0FDFA 100%)',
          icon: 'mdi-heart-pulse',
          details: [
            { label: 'Inpatient limit', value: quote.inpatient_limit || '—' },
            { label: 'Premium', value: quote.cover_premium || '—' },
            { label: LABEL_QUOTE_REF, value: quote.id ? `#${quote.id}` : '—' },
          ],
          progress: 0,
          showStepper: false,
          ctaLabel: CTA_RESUME,
          quote,
          type: 'health',
          ...buildQuoteItemDate(quote),
        });
      });

      (this.dpQuotes || []).forEach((quote, i) => {
        items.push({
          key: `dp-${quote.id || i}`,
          domId: `quotes-dp-${i}`,
          productLabel: 'Domestic Package',
          statusLabel: 'Draft',
          subtitle: 'Home & contents cover',
          accent: '#EA580C',
          iconBg: 'linear-gradient(135deg, #FFEDD5 0%, #FFF7ED 100%)',
          icon: 'mdi-home-circle-outline',
          details: [
            { label: 'Home option', value: quote.home_option || '—' },
            { label: 'Premium', value: quote.premium || '—' },
            { label: LABEL_QUOTE_REF, value: quote.id ? `#${quote.id}` : '—' },
          ],
          progress: 0,
          showStepper: false,
          ctaLabel: CTA_RESUME,
          quote,
          type: 'dp',
          ...buildQuoteItemDate(quote),
        });
      });

      (this.travelQuotes || []).forEach((quote, i) => {
        const quoteId = quote.quoteId || quote.id;
        const travelStatus = quote.stepCode === 'POLICY_PAYMENT' ? STATUS_READY_TO_PAY : STATUS_IN_PROGRESS;
        const destination = quote.region || quote.destinationCountry || quote.destination;
        const travelChannel = (!this.isAgent && quote.journey === 'direct') ? 'customer' : 'agent';
        items.push({
          key: `travel-${quoteId || i}`,
          domId: `quotes-travel-${i}`,
          productLabel: 'Travel Insurance',
          statusLabel: travelStatus,
          subtitle: destination || quote.prodCode || 'Travel cover',
          accent: '#0284c7',
          iconBg: 'linear-gradient(135deg, #E0F2FE 0%, #F0F9FF 100%)',
          icon: 'mdi-airplane',
          details: [
            { label: 'Destination', value: destination || '—' },
            { label: 'Plan', value: quote.prodCode || quote.productLabel || '—' },
            { label: 'Premium', value: formatTravelPremium(quote) },
          ],
          progress: 0,
          showStepper: false,
          ctaLabel: CTA_RESUME,
          quote: { ...quote, quoteId },
          type: 'travel',
          channel: travelChannel,
          ...buildQuoteItemDate(quote),
        });
      });

      items.sort((a, b) => b.timestamp - a.timestamp);
      return items;
    },
  },
  watch: {
    loading: {
      handler(val) {
        this.$emit('loading-change', val);
      },
      immediate: true,
    },
    allDisplayItems: {
      handler(items) {
        if (!this.loading) {
          this.$emit('saved-quotes-loaded', items.length);
        }
      },
    },
    searchQuery() {
      this.currentPage = 1;
    },
    selectedProduct() {
      this.currentPage = 1;
    },
    dateFrom() {
      this.currentPage = 1;
    },
    dateTo() {
      this.currentPage = 1;
    },
    filteredItems() {
      if (this.currentPage > this.totalPages) {
        this.currentPage = this.totalPages;
      }
    },
  },
  async mounted() {
    if (this.isAgent) {
      await this.$store.dispatch('portalCache/fetchAgentQuotes');
    } else {
      await this.$store.dispatch('portalCache/fetchCustomerQuotes');
    }
    this.emitSavedQuotes();
  },
  methods: {
    resetFilters() {
      this.searchQuery = '';
      this.selectedProduct = 'all';
      this.dateFrom = '';
      this.dateTo = '';
      this.currentPage = 1;
    },
    goToPage(page) {
      if (page < 1 || page > this.totalPages || page === this.currentPage) return;
      this.currentPage = page;
      if (typeof window !== 'undefined') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
    resumeContext() {
      return {
        router: this.$router,
        store: this.$store,
        toast: this.$toast,
        email: this.identity?.email || this.authenticatedUser?.email,
        channel: this.isAgent ? 'agent' : 'customer',
      };
    },
    async resumeItem(item) {
      if (this.resumingKey || !item?.quote) return;
      this.resumingKey = item.key;
      try {
        const ctx = this.resumeContext();
        switch (item.type) {
          case 'motor':
            if (this.isAgent) {
              await this.$router.push({
                name: 'AgentSale',
                query: { quoteId: String(item.quote.quoteId), journey: 'direct' },
              });
            } else {
              await resumeMotorQuote(item.quote, ctx);
            }
            break;
          case 'pa':
            if (this.isAgent) {
              if (isPaQuoteIssued(item.quote)) {
                await this.$router.push({ name: 'AgentConversions' });
                return;
              }
              if (isPaQuoteAwaitingIssuance(item.quote)) {
                await this.$router.push({
                  name: 'AgentPaFinalization',
                  params: { quoteId: String(item.quote.quoteId) },
                });
                return;
              }
              await this.$router.push({
                name: 'AgentPaSale',
                query: { quoteId: String(item.quote.quoteId) },
              });
            } else {
              await resumePaQuote(item.quote, ctx);
            }
            break;
          case 'health':
            await resumeHealthQuote(item.quote, ctx);
            break;
          case 'dp':
            await resumeDpQuote(item.quote, ctx);
            break;
          case 'travel':
            await resumeTravelQuote(item.quote, {
              ...ctx,
              channel: this.isAgent ? 'agent' : item.channel,
              email: this.identity?.email || this.authenticatedUser?.email,
              getCoverageAmountByRegion: this.getCoverageAmountByRegion.bind(this),
            });
            break;
          default:
            break;
        }
      } catch {
        this.$toast.error('Could not open this quote. Please try again.', 'Resume quote');
      } finally {
        this.resumingKey = null;
      }
    },
    emitSavedQuotes() {
      this.$emit('saved-quotes-loaded', this.allDisplayItems.length);
    },
    filterCar(quote) {
      return quote.details?.risks?.find((s) => s.selected === 'Y') || quote.details?.risks?.[0];
    },
    getCoverageAmountByRegion(region, planType) {
      const coverageMap = {
        'Africa/Asia': 15000,
        Europe: 40000,
        'Worldwide Basic': 40000,
        'Worldwide Plus': 75000,
        'Worldwide Extra': 150000,
        'Worldwide Classic': 60000,
        'Worldwide Premium': 100000,
      };
      if (planType === 'student') {
        if (region && region.toLowerCase().includes('premium')) return 100000;
        return 60000;
      }
      const normalizedRegion = region || 'Africa/Asia';
      if (coverageMap[normalizedRegion]) return coverageMap[normalizedRegion];
      const regionLower = normalizedRegion.toLowerCase();
      if (regionLower.includes('africa') || regionLower.includes('asia')) return 15000;
      if (regionLower.includes('europe')) return 40000;
      if (regionLower.includes('worldwide') && regionLower.includes('extra')) return 150000;
      if (regionLower.includes('worldwide') && regionLower.includes('plus')) return 75000;
      if (regionLower.includes('worldwide')) return 40000;
      return 15000;
    },
    formatTravelPlanType(planType) {
      if (!planType) return 'Unknown';
      const planTypeMap = { individual: 'Individual', family: 'Family', student: 'Student' };
      return planTypeMap[planType] || planType.charAt(0).toUpperCase() + planType.slice(1);
    },
    progress(quote) {
      const risk = this.filterCar(quote);
      if (!risk) return 0;
      if (quote.step === 'POLICY_PAYMENT') return 100;
      if ('mainBranchCode' in quote && quote.mainBranchCode !== '') return 80;
      if ('engineNo' in risk.car && risk.car.engineNo !== '') return 66.66;
      if ('coverFrom' in quote && quote.coverFrom !== '') return 33.33;
      return 0;
    },
  },
};
</script>

<style lang="scss" scoped>
.quotes-shell--hidden {
  display: none;
}
/* =========================================
   Quote loading shimmer
   ========================================= */

.quotes-skeleton {
  background: linear-gradient(
    90deg,
    #eeeeee 25%,
    #f7f7f7 50%,
    #eeeeee 75%
  );
  background-size: 200% 100%;
  animation: quotes-shimmer 1.5s infinite;
  border-radius: 6px;
}

@keyframes quotes-shimmer {
  0% {
    background-position: -200% 0;
  }

  100% {
    background-position: 200% 0;
  }
}

.quote-card--skeleton {
  pointer-events: none;
}

.quotes-skeleton__icon {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  flex-shrink: 0;
}

.quotes-skeleton__title {
  width: 180px;
  height: 18px;
}

.quotes-skeleton__subtitle {
  width: 130px;
  height: 14px;
  margin-top: 8px;
}

.quotes-skeleton__label {
  width: 70px;
  height: 10px;
}

.quotes-skeleton__value {
  width: 110px;
  height: 14px;
  margin-top: 6px;
}

.quotes-skeleton__button {
  width: 125px;
  height: 36px;
  border-radius: 6px;
}

.quotes-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 3rem 1rem;
  color: #64748b;
  font-size: 0.9rem;
}

.quotes-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.quote-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 1.25rem 1.25rem 1rem;
  box-shadow: 0 4px 18px rgba(24, 70, 121, 0.06);
}

@media (min-width: 768px) {
  .quote-card {
    padding: 1.5rem 1.75rem 1.25rem;
  }
}

.quote-card__hero {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 1rem;
}

.quote-card__icon {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.quote-card__intro {
  flex: 1;
  min-width: 0;
}

.quote-card__title-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.quote-card__title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 800;
  color: #1e4276;
}

.quote-card__badge {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #0369a1;
  background: #e0f2fe;
  border-radius: 999px;
  padding: 3px 10px;
}

.quote-card__subtitle {
  margin: 4px 0 0;
  font-size: 0.875rem;
  color: #64748b;
}

.quote-card__details {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  padding: 14px 16px;
  background: #f8fafc;
  border: 1px solid #eef2f6;
  border-radius: 12px;
  margin-bottom: 1rem;
}

@media (min-width: 640px) {
  .quote-card__details {
    grid-template-columns: repeat(3, 1fr);
  }
}

.quote-detail {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.quote-detail__label {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #94a3b8;
}

.quote-detail__value {
  font-size: 0.9rem;
  font-weight: 700;
  color: #1e4276;
  line-height: 1.35;
  word-break: break-word;
}

.quote-card__stepper {
  margin-bottom: 1rem;
  overflow-x: auto;
}

.quote-card__stepper .v-stepper {
  box-shadow: none !important;
}

.quote-card__actions {
  display: flex;
  justify-content: flex-end;
}

.quotes-empty {
  text-align: center;
  padding: 3rem 1.5rem;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
}

.quotes-empty__icon {
  margin-bottom: 1rem;
}

.quotes-empty__title {
  margin: 0 0 8px;
  font-size: 1.125rem;
  font-weight: 800;
  color: #1e4276;
}

.quotes-empty__text {
  margin: 0 0 1.25rem;
  font-size: 0.9rem;
  color: #64748b;
  max-width: 320px;
  margin-left: auto;
  margin-right: auto;
}

.quotes-empty__cta {
  display: inline-flex;
  align-items: center;
  padding: 10px 20px;
  background: #184679;
  color: #fff !important;
  font-size: 0.875rem;
  font-weight: 700;
  border-radius: 10px;
  text-decoration: none;
}

/* ── Filters Toolbar ── */
.quotes-toolbar {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 1rem 1.25rem;
  margin-bottom: 1.25rem;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.04);
}

.quotes-toolbar__row {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
}

.quotes-toolbar__search {
  flex: 1 1 240px;
  min-width: 220px;
  display: flex;
  align-items: center;
  background: #f8fafc;
  border: 1.5px solid #e2e8f0;
  border-radius: 9px;
  padding: 0 12px;
  height: 42px;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.quotes-toolbar__search:focus-within {
  border-color: #00aeef;
  box-shadow: 0 0 0 3px rgba(0, 174, 239, 0.12);
  background: #fff;
}

.quotes-toolbar__search-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.875rem;
  color: #1e4276;
}

.quotes-toolbar__search-input::placeholder {
  color: #94a3b8;
}

.quotes-toolbar__clear-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
}

.quotes-toolbar__field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.quotes-toolbar__label {
  font-size: 0.72rem;
  font-weight: 700;
  color: #1e4276;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.quotes-toolbar__select {
  background: #f8fafc;
  border: 1.5px solid #e2e8f0;
  border-radius: 9px;
  padding: 0 10px;
  height: 42px;
  font-size: 0.875rem;
  color: #1e4276;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.2s;
}

.quotes-toolbar__select:focus {
  outline: none;
  border-color: #00aeef;
  box-shadow: 0 0 0 3px rgba(0, 174, 239, 0.12);
}

.quotes-toolbar__date-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #f8fafc;
  border: 1.5px solid #e2e8f0;
  border-radius: 9px;
  padding: 0 10px;
  height: 42px;
  transition: border-color 0.2s;
}

.quotes-toolbar__date-wrap:focus-within {
  border-color: #00aeef;
  box-shadow: 0 0 0 3px rgba(0, 174, 239, 0.12);
  background: #fff;
}

.quotes-toolbar__date-input {
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.85rem;
  color: #1e4276;
  font-weight: 500;
}

.quotes-toolbar__reset-btn {
  display: inline-flex;
  align-items: center;
  height: 42px;
  padding: 0 14px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  font-size: 0.8rem;
  font-weight: 700;
  border-radius: 9px;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  white-space: nowrap;
}

.quotes-toolbar__reset-btn:hover {
  background: #fee2e2;
  border-color: #fca5a5;
}

.quotes-toolbar__meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid #f1f5f9;
}

.quotes-toolbar__count {
  font-size: 0.8rem;
  color: #64748b;
}

.quotes-toolbar__count strong {
  color: #1e4276;
}

.quotes-toolbar__filtered-tag {
  font-size: 0.7rem;
  font-weight: 700;
  color: #0284c7;
  background: #e0f2fe;
  padding: 2px 8px;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.quote-card__date {
  display: inline-flex;
  align-items: center;
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  margin-left: auto;
}

/* ── Pagination ── */
.quotes-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 1rem 0.25rem 0.5rem;
  flex-wrap: wrap;
}

.quotes-pagination__info {
  font-size: 0.85rem;
  color: #64748b;
}

.quotes-pagination__controls {
  display: flex;
  align-items: center;
  gap: 6px;
}

.quotes-pagination__btn {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 0.825rem;
  font-weight: 600;
  color: #1e4276;
  cursor: pointer;
  transition: all 0.15s ease;
}

.quotes-pagination__btn:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.quotes-pagination__btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.quotes-pagination__pages {
  display: flex;
  align-items: center;
  gap: 4px;
}

.quotes-pagination__page-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #fff;
  font-size: 0.825rem;
  font-weight: 600;
  color: #1e4276;
  cursor: pointer;
  transition: all 0.15s ease;
}

.quotes-pagination__page-btn:hover:not(.quotes-pagination__page-btn--active) {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.quotes-pagination__page-btn--active {
  background: #184679;
  border-color: #184679;
  color: #fff;
  font-weight: 700;
}

.quotes-empty__cta-btn {
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  background: #184679;
  color: #fff;
  font-size: 0.85rem;
  font-weight: 700;
  border-radius: 8px;
  border: none;
  cursor: pointer;
}
</style>
